import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { APIList } from 'src/interfaces'
import { Entities } from 'src/enums'
import { Sensor } from 'src/api'
import { useApiInstance } from './useApiInstance'
import {
  buildRequestParams,
  ObjectType,
  transformPage,
  transformPerPage,
} from './helpers'

interface RequestParams {
  params?: Partial<AdminSensorsMapViewGetParams>;
  paramsTransform?: Array<(params: Record<string, any>) => Record<string, any>>;
  options?: Omit<
    UseQueryOptions<unknown, Error, APIList<Sensor>>,
    'queryKey' | 'queryFn'
  >;
}

interface AdminSensorsMapViewGetParams {
  limit: number;
  offset: number;
  sort: string;
  contractorId: number;
  stateId: number;
  lastHeard: string;
  lastPing: string;
  synchronizationPeriod: string;
  accelerationTime: string;
  batteryTime: string;
  dewpointTime: string;
  humidityTime: string;
  lightTime: string;
  locationTime: string;
  locationGpsTime: string;
  orientationTime: string;
  pressureTime: string;
  signalStrengthTime: string;
  temperatureTime: string;
  batteryLevel: number;
  signalStrength: number;
  search: string;
  currentSensorConfiguration: string;
  orderNumber: string;
  orderDateFrom: string;
  orderDateTo: string;
}

const schema: ObjectType['schema'] = {
  limit: 'number',
  offset: 'number',
  sort: 'string',
  contractorId: 'number',
  stateId: 'number',
  lastHeard: 'string',
  lastPing: 'string',
  synchronizationPeriod: 'string',
  accelerationTime: 'string',
  batteryTime: 'string',
  dewpointTime: 'string',
  humidityTime: 'string',
  lightTime: 'string',
  locationTime: 'string',
  locationGpsTime: 'string',
  orientationTime: 'string',
  pressureTime: 'string',
  signalStrengthTime: 'string',
  temperatureTime: 'string',
  batteryLevel: 'number',
  signalStrength: 'number',
  search: 'string',
  currentSensorConfiguration: 'string',
  orderNumber: 'string',
  orderDateFrom: 'string',
  orderDateTo: 'string',
}

export const useSensorMapViewListRequest = ({
  params = {},
  paramsTransform = [],
  options,
}: RequestParams = {}) => {
  const api = useApiInstance()

  const transformedParams = [
    transformPerPage,
    transformPage,
    ...paramsTransform,
  ].reduce((param, transform) => transform(param), params)

  const queryParams = buildRequestParams<AdminSensorsMapViewGetParams>(
    transformedParams,
    schema,
  )

  return useQuery<unknown, Error, APIList<Sensor>>(
    [Entities.Sensors, JSON.stringify(transformedParams)],
    async () => {
      const {
        limit,
        offset,
        sort,
        contractorId,
        stateId,
        lastHeard,
        lastPing,
        synchronizationPeriod,
        accelerationTime,
        batteryTime,
        dewpointTime,
        humidityTime,
        lightTime,
        locationTime,
        locationGpsTime,
        orientationTime,
        pressureTime,
        signalStrengthTime,
        temperatureTime,
        batteryLevel,
        signalStrength,
        search,
        currentSensorConfiguration,
        orderNumber,
        orderDateFrom,
        orderDateTo,
      } = queryParams
      const { data } = await api.v1AdminSensorsMapViewGet(
        limit,
        offset,
        sort,
        contractorId,
        stateId,
        lastHeard,
        batteryLevel,
        search,
        lastPing,
        synchronizationPeriod,
        accelerationTime,
        batteryTime,
        dewpointTime,
        humidityTime,
        lightTime,
        locationTime,
        locationGpsTime,
        orientationTime,
        pressureTime,
        signalStrengthTime,
        temperatureTime,
        signalStrength,
        currentSensorConfiguration,
        undefined, // isBleEnabled — not filterable on the map view
        undefined, // typeId — not filterable on the map view
        orderNumber,
        orderDateFrom,
        orderDateTo,
      )
      return data
    },
    {
      enabled: Object.keys(transformedParams).length > 0,
      ...options,
    },
  )
}
